import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Edit, Shield } from "lucide-react";
import { toast } from "sonner";

import { logoColor as LOGO_URL } from "@/lib/logos";

export default function AdminUsuarios() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [techs, setTechs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "", cargo: "tecnico", password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadTechs(); }, []);

  const loadTechs = async () => {
    try { const r = await api.get("/api/auth/technicians"); setTechs(r.data); } catch {}
  };

  const uf = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const openNew = () => { setEditing(null); setForm({ nombre: "", email: "", telefono: "", cargo: "tecnico", password: "" }); setShowForm(true); };
  const openEdit = (t) => { setEditing(t); setForm({ nombre: t.nombre, email: t.email, telefono: t.telefono, cargo: t.cargo, password: "" }); setShowForm(true); };

  const handleSave = async () => {
    if (!form.nombre || !form.email) { toast.error("Nombre y email requeridos"); return; }
    if (!editing && !form.password) { toast.error("Contraseña requerida para nuevo técnico"); return; }
    setSaving(true);
    try {
      if (editing) {
        const data = { nombre: form.nombre, email: form.email, telefono: form.telefono, cargo: form.cargo };
        if (form.password) data.password = form.password;
        await api.put(`/api/auth/technicians/${editing.id}`, data);
        toast.success("Técnico actualizado");
      } else {
        await api.post("/api/auth/technicians", form);
        toast.success("Técnico creado");
      }
      setShowForm(false);
      loadTechs();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (t) => {
    try {
      await api.put(`/api/auth/technicians/${t.id}`, { estado: t.estado === "activo" ? "inactivo" : "activo" });
      loadTechs();
      toast.success(`Técnico ${t.estado === "activo" ? "desactivado" : "activado"}`);
    } catch { toast.error("Error"); }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-7" />
            <span className="text-sm font-semibold text-[#384091]">Gestión de Técnicos</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-[#0FB4B7] text-white" onClick={openNew}><Plus className="h-3.5 w-3.5 mr-1" /> Nuevo técnico</Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/dashboard")}><ArrowLeft className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-blue-50 rounded-lg p-3 mb-4 flex items-start gap-2">
          <Shield className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-blue-700 font-semibold">Contraseñas hasheadas con bcrypt · JWT con expiración</p>
            <p className="text-[10px] text-blue-500 mt-0.5">El admin asigna contraseñas. Solo el admin crea/edita usuarios.</p>
          </div>
        </div>
        <div className="space-y-2">
          {techs.map((t) => (
            <Card key={t.id} className="border-0 shadow-sm">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-800">{t.nombre}</p>
                  <p className="text-xs text-gray-500">{t.email} · {t.telefono}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={t.cargo === "admin" ? "bg-indigo-50 text-indigo-700 border-0" : "bg-gray-100 text-gray-600 border-0"}>{t.cargo}</Badge>
                  <Badge className={t.estado === "activo" ? "bg-emerald-50 text-emerald-700 border-0 cursor-pointer" : "bg-red-50 text-red-600 border-0 cursor-pointer"}
                    onClick={() => toggleStatus(t)}>{t.estado}</Badge>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(t)}><Edit className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="text-[#384091]">{editing ? "Editar" : "Nuevo"} Técnico</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre completo *</Label><Input value={form.nombre} onChange={(e) => uf("nombre", e.target.value)} className="mt-1" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => uf("email", e.target.value)} className="mt-1" /></div>
            <div><Label>Teléfono</Label><Input value={form.telefono} onChange={(e) => uf("telefono", e.target.value)} className="mt-1" /></div>
            <div>
              <Label>Contraseña {editing ? "(dejar vacío para no cambiar)" : "*"}</Label>
              <Input type="password" value={form.password} onChange={(e) => uf("password", e.target.value)} className="mt-1" />
              <p className="text-[10px] text-gray-400 mt-1">Se almacenará encriptada con bcrypt</p>
            </div>
            <div>
              <Label>Cargo</Label>
              <Select value={form.cargo} onValueChange={(v) => uf("cargo", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tecnico">Técnico</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button className="bg-[#0FB4B7] text-white" onClick={handleSave} disabled={saving}>
                {saving ? "Guardando..." : editing ? "Actualizar" : "Crear técnico"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
